import FilterIcon from "../../assets/icons/FilterIcon";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import Loader from "../../components/common/Loader";
import TransactionList from "../../components/common/transactions/TransactionList";
import {
  extractTransactionListFromResponse,
  formatTransactionDate,
  resolveTransactionsHasMore,
} from "../../components/common/transactions/transactionUtils";
import TransactionsFilterPanel from "./components/TransactionsFilterPanel";
import gluedin from "gluedin-shorts-js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const paymentModule = new gluedin.GluedInPaymentModule();
const TRANSACTION_PAGE_LIMIT = 15;

const getTransactionKey = (transaction: any) =>
  String(
    transaction?.transactionId ||
      transaction?.id ||
      transaction?._id ||
      transaction?.txId ||
      transaction?.referenceId ||
      `${
        transaction?.createdTimestamp ||
        transaction?.createdAt ||
        transaction?.created_at ||
        transaction?.date ||
        ""
      }-${
        transaction?.transactionAmount ??
        transaction?.amount ??
        transaction?.coins ??
        transaction?.value ??
        ""
      }`,
  );

const mergeTransactions = (current: any[], incoming: any[]) => {
  if (!incoming.length) return current;
  const existingKeys = new Set(current.map((item) => getTransactionKey(item)));
  const merged = [...current];

  incoming.forEach((item) => {
    const key = getTransactionKey(item);
    if (existingKeys.has(key)) return;
    existingKeys.add(key);
    merged.push(item);
  });

  return merged;
};

const resolveTransactionDate = (transaction: any) => {
  const dateValue =
    transaction?.createdTimestamp ||
    transaction?.createdAt ||
    transaction?.created_at ||
    transaction?.date ||
    transaction?.transactionDate ||
    "";
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const resolveTransactionType = (transaction: any) =>
  String(
    transaction?.transactionType ||
      transaction?.eventType ||
      transaction?.transactionReason ||
      transaction?.event ||
      transaction?.type ||
      "",
  ).trim();

type TransactionFilters = {
  from: Date | null;
  to: Date | null;
  type: string;
};

const cloneFilters = (filters: TransactionFilters): TransactionFilters => ({
  from: filters.from ? new Date(filters.from) : null,
  to: filters.to ? new Date(filters.to) : null,
  type: filters.type,
});

const areFiltersEqual = (
  first: TransactionFilters,
  second: TransactionFilters,
) => {
  const firstFrom = first.from ? first.from.getTime() : null;
  const secondFrom = second.from ? second.from.getTime() : null;
  const firstTo = first.to ? first.to.getTime() : null;
  const secondTo = second.to ? second.to.getTime() : null;
  return (
    firstFrom === secondFrom &&
    firstTo === secondTo &&
    first.type.trim() === second.type.trim()
  );
};

const formatFilterDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Transactions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defaultLanguage = localStorage.getItem("defaultLanguage");
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [offset, setOffset] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const filterBtnRef = useRef<HTMLButtonElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isPageRequestInFlightRef = useRef(false);
  const pendingResetRequestRef = useRef(false);
  const hasUserScrolledRef = useRef(false);
  const hasMoreRef = useRef(true);
  const [filters, setFilters] = useState<TransactionFilters>({
    from: null,
    to: null,
    type: "",
  });
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>({
    from: null,
    to: null,
    type: "",
  });
  const appliedFiltersRef = useRef<TransactionFilters>({
    from: null,
    to: null,
    type: "",
  });

  const resolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const fetchTransactionsPage = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (isPageRequestInFlightRef.current) {
        if (!append) {
          pendingResetRequestRef.current = true;
        }
        return;
      }
      if (append && !hasMoreRef.current) return;

      isPageRequestInFlightRef.current = true;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setHasMore(true);
        hasMoreRef.current = true;
        setIsLoading(true);
      }

      try {
        const requestPayload: Record<string, any> = {
          limit: TRANSACTION_PAGE_LIMIT,
          offset: nextOffset,
        };

        const appliedFilters = appliedFiltersRef.current;
        if (appliedFilters.from) {
          requestPayload.fromDate = formatFilterDateParam(appliedFilters.from);
        }
        if (appliedFilters.to) {
          requestPayload.toDate = formatFilterDateParam(appliedFilters.to);
        }
        const transactionType = appliedFilters.type.trim();
        if (transactionType) {
          requestPayload.transactionType = transactionType;
        }

        const response = await paymentModule.getTransactionList(requestPayload);

        const list = extractTransactionListFromResponse(response);
        const pageHasMore = resolveTransactionsHasMore(
          response,
          list.length,
          TRANSACTION_PAGE_LIMIT,
          nextOffset,
        );

        setTransactions((prev) =>
          append ? mergeTransactions(prev, list) : list,
        );
        setOffset(nextOffset);
        setHasMore(pageHasMore);
        hasMoreRef.current = pageHasMore;
      } catch (error) {
        console.error(error);
        if (!append) {
          setTransactions([]);
        }
        setHasMore(false);
        hasMoreRef.current = false;
      } finally {
        isPageRequestInFlightRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
        if (pendingResetRequestRef.current) {
          pendingResetRequestRef.current = false;
          fetchTransactionsPage(1, false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    appliedFiltersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    pendingResetRequestRef.current = false;
    hasUserScrolledRef.current = false;
    fetchTransactionsPage(1, false);
  }, [fetchTransactionsPage, filters.from, filters.to, filters.type]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    if (!isFilterOpen) return;

    const handleOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (filterRef.current?.contains(target)) return;
      if (filterBtnRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        (target.closest(".react-datepicker") ||
          target.closest(".react-datepicker-popper"))
      ) {
        return;
      }
      setIsFilterOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    const markUserScrollIntent = () => {
      hasUserScrolledRef.current = true;
    };
    window.addEventListener("scroll", markUserScrollIntent, { passive: true });
    return () => {
      window.removeEventListener("scroll", markUserScrollIntent);
    };
  }, []);

  useEffect(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasUserScrolledRef.current) return;
        hasUserScrolledRef.current = false;
        fetchTransactionsPage(offset + 1, true);
      },
      {
        root: null,
        rootMargin: "180px 0px",
        threshold: 0,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchTransactionsPage, hasMore, isLoading, isLoadingMore, offset]);

  const typeOptions = useMemo(() => {
    const uniqueTypes = new Set<string>();
    transactions.forEach((transaction) => {
      const typeValue = resolveTransactionType(transaction);
      if (typeValue) {
        uniqueTypes.add(typeValue);
      }
    });
    return Array.from(uniqueTypes);
  }, [transactions]);

  const transactionList = useMemo(() => {
    const hasDateFilter = filters.from || filters.to;
    const hasTypeFilter = filters.type;
    if (!hasDateFilter && !hasTypeFilter) return transactions;

    const fromDate = filters.from;
    const toDate = filters.to;
    const toDateEnd = toDate
      ? new Date(
          toDate.getFullYear(),
          toDate.getMonth(),
          toDate.getDate(),
          23,
          59,
          59,
          999,
        )
      : null;
    const typeFilter = filters.type.trim().toLowerCase();

    return transactions.filter((transaction) => {
      if (typeFilter) {
        const transactionType =
          resolveTransactionType(transaction).toLowerCase();
        if (!transactionType || transactionType !== typeFilter) {
          return false;
        }
      }

      if (hasDateFilter) {
        const transactionDate = resolveTransactionDate(transaction);
        if (!transactionDate) return false;
        if (fromDate && transactionDate < fromDate) return false;
        if (toDateEnd && transactionDate > toDateEnd) return false;
      }

      return true;
    });
  }, [filters.from, filters.to, filters.type, transactions]);

  const hasActiveDraftFilters = Boolean(
    draftFilters.from || draftFilters.to || draftFilters.type,
  );
  const hasPendingChanges = !areFiltersEqual(filters, draftFilters);

  const dateRangeLabel = resolveLabel(
    "label-select-date-range",
    "Select Date Range:",
  );
  const resetLabel = resolveLabel("label-reset", "Reset");
  const fromLabel = resolveLabel("label-from", "From");
  const toLabel = resolveLabel("label-to", "To");
  const typeLabel = resolveLabel("label-type", "Type");
  const selectTypeLabel = resolveLabel(
    "label-select-transaction-type",
    "Select Transaction Type",
  );
  const applyLabel = resolveLabel("label-apply", "Apply");
  const loadingMoreLabel = resolveLabel(
    "label-loading-more-transactions",
    "Loading more transactions...",
  );

  return (
    <div className="full-sec"><div className="right-sec profile-content-list transactions-page">
        <div className="full-box transactions-full-box">
          <div className="back-header transactions-header">
            <ul className="list list-inline">
              <li className="back-btn">
                <button
                  className="back-btn-a"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  {defaultLanguage === "en" ? (
                    <LeftArrowIcon />
                  ) : (
                    <RightArrowIcon />
                  )}
                  {t("back-btn")}
                </button>
              </li>
              <li className="common-heading">
                {resolveLabel("label-transactions", "Transactions")}
              </li>
              <li className="transactions-header__action">
                {/* <button
                  type="button"
                  className="transactions-support-btn"
                  onClick={() => navigate("/support")}
                >
                  {resolveLabel("label-support", "Support")}
                </button> */}
              </li>
            </ul>
          </div>

          {isLoading && <Loader />}

          {!isLoading && (
            <div className="transactions-content">
              <button
                type="button"
                className="transactions-filter-btn"
                aria-label={resolveLabel("label-filter", "Filter")}
                aria-expanded={isFilterOpen}
                aria-controls="transactions-filter-panel"
                ref={filterBtnRef}
                onClick={() =>
                  setIsFilterOpen((prev) => {
                    const nextState = !prev;
                    if (nextState) {
                      setDraftFilters(cloneFilters(filters));
                    }
                    return nextState;
                  })
                }
              >
                <FilterIcon />
              </button>
              {isFilterOpen && (
                <TransactionsFilterPanel
                  ref={filterRef}
                  filters={draftFilters}
                  setFilters={setDraftFilters}
                  hasActiveFilters={hasActiveDraftFilters}
                  hasPendingChanges={hasPendingChanges}
                  dateRangeLabel={dateRangeLabel}
                  resetLabel={resetLabel}
                  applyLabel={applyLabel}
                  fromLabel={fromLabel}
                  toLabel={toLabel}
                  typeLabel={typeLabel}
                  selectTypeLabel={selectTypeLabel}
                  typeOptions={typeOptions}
                  formatDate={formatTransactionDate}
                  onApply={() => {
                    hasUserScrolledRef.current = false;
                    setFilters(cloneFilters(draftFilters));
                    setIsFilterOpen(false);
                  }}
                />
              )}
              {transactionList.length === 0 && (
                <div className="transactions-empty">
                  {resolveLabel(
                    "label-no-transactions",
                    "No transactions yet.",
                  )}
                </div>
              )}
              {transactionList.length > 0 && (
                <TransactionList
                  transactions={transactionList}
                  className="transactions-list"
                  showDateInMiddle={true}
                />
              )}
              {isLoadingMore && (
                <div className="transactions-load-more">{loadingMoreLabel}</div>
              )}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="transactions-infinite-trigger"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
